import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "../lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MessageSquare, Loader2, ArrowLeft } from "lucide-react";

const confirmationSchema = z.object({
  code: z.string().min(6, "Confirmation code must be at least 6 characters"),
});

type ConfirmationFormData = z.infer<typeof confirmationSchema>;

interface EmailConfirmationProps {
  email: string;
  onSuccess: () => void;
  onBack: () => void;
}

export const EmailConfirmation: React.FC<EmailConfirmationProps> = ({
  email,
  onSuccess,
  onBack,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmationFormData>({
    resolver: zodResolver(confirmationSchema),
  });

  const onSubmit = async (data: ConfirmationFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      if (!supabase) {
        throw new Error("Supabase client not available");
      }
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: data.code,
        type: "signup",
      });

      if (error) throw error;
      onSuccess();
    } catch (error: unknown) {
      console.error("Confirmation failed:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      setError(errorMessage || "Invalid confirmation code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError(null);

    try {
      if (!supabase) {
        throw new Error("Supabase client not available");
      }
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email,
      });

      if (error) throw error;
      setError("Confirmation code resent! Please check your email.");
    } catch (error: unknown) {
      console.error("Resend failed:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      setError(errorMessage || "Failed to resend confirmation code.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center space-x-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <MessageSquare className="h-4 w-4" />
            </div>
            <CardTitle className="text-2xl font-bold">Maat AI</CardTitle>
          </div>
          <CardDescription className="text-center">
            Check your email for a confirmation code
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                We've sent a 6-digit confirmation code to:
              </p>
              <p className="font-medium">{email}</p>
            </div>

            {error && (
              <Alert
                variant={error.includes("resent") ? "default" : "destructive"}
              >
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="code">Confirmation Code</Label>
              <Input
                id="code"
                type="text"
                placeholder="Enter 6-digit code"
                maxLength={6}
                {...register("code")}
                disabled={isLoading}
                className="text-center text-lg tracking-widest"
              />
              {errors.code && (
                <p className="text-sm text-destructive">
                  {errors.code.message}
                </p>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm Email
            </Button>

            <div className="flex flex-col space-y-2">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleResendCode}
                disabled={isResending}
              >
                {isResending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Resend Code
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={onBack}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Registration
              </Button>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};
